import { CancelToken } from 'axios';
import { useCallback, useEffect, useState, useRef } from 'react';

import { usePrevious } from '@hivebrite/react-hooks';

import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';

import { HTTP_STATUS_CODE_403 } from '../constants/httpStatusCode';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import fetchCategories from '../requests/fetchCategories';

const useTopicsCategories = () => {
  const {
    areFetchedAllCategories,
    selectedSuperCategoryId,
    setAreFetchedAllCategories,
    setSelectedSuperCategory,
    setTotalAllCategories,
    setUserAuthorizationInfo,
  } = useTopicsIndexContext();

  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [totalCategories, setTotalCategories] = useState(0);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);

  const cancelTokenRef = useRef(null);
  const fetchDone = useRef(false);

  const prevPage = usePrevious(page);

  const loadNextPage = useCallback(
    () => setPage((previousPage) => previousPage + 1),
    [],
  );

  const handleFetchCategories = useCallback(async () => {
    if (isFetchingCategories && cancelTokenRef.current) {
      cancelTokenRef.current.cancel(CANCEL_FETCH_MESSAGE);
    }
    cancelTokenRef.current = CancelToken.source();

    setIsFetchingCategories(true);
    fetchDone.current = false;

    const response = await fetchCategories({
      cancelToken: cancelTokenRef?.current,
      categories_with_visible_topics: true,
      page,
      selectedSuperCategoryId,
    });

    if (response?.response?.status === HTTP_STATUS_CODE_403) {
      setUserAuthorizationInfo({ isAuthorized: false, error: response });
    } else {
      setUserAuthorizationInfo({ isAuthorized: true });

      if (response?.categories) {
        // We set info about the selected Super Category
        if (selectedSuperCategoryId) {
          setSelectedSuperCategory(response.superCategory);
        }
        // We add new categories to the existing list
        setCategories([...categories, ...response.categories]);
        setTotalCategories(response.total);
        setIsFetchingCategories(false);

        fetchDone.current = true;
      }
    }
  }, [
    categories,
    isFetchingCategories,
    page,
    selectedSuperCategoryId,
    setSelectedSuperCategory,
    setUserAuthorizationInfo,
  ]);

  useEffect(() => {
    if (prevPage !== page) {
      handleFetchCategories();
    }
  }, [prevPage, page, handleFetchCategories]);

  useEffect(() => {
    if (fetchDone.current && !areFetchedAllCategories) {
      setAreFetchedAllCategories(categories.length === totalCategories);
      setTotalAllCategories(totalCategories);

      fetchDone.current = false;
    }
  }, [
    areFetchedAllCategories,
    categories,
    totalCategories,
    setAreFetchedAllCategories,
    setTotalAllCategories,
  ]);

  return {
    categories,
    totalCategories,
    isFetchingCategories,
    loadNextPage,
  };
};

export default useTopicsCategories;
